
import React, { createContext, useContext, useState, useEffect } from 'react';

export type UnitSystem = 'METRIC' | 'IMPERIAL' | 'BRITISH';

interface UnitContextType {
  unitSystem: UnitSystem;
  setUnitSystem: (system: UnitSystem) => void;
  toggleUnitSystem: () => void;
  isMetric: boolean;
}

const UnitContext = createContext<UnitContextType | undefined>(undefined);

export function UnitProvider({ children }: { children: React.ReactNode }) {
  const [unitSystem, setUnitSystemState] = useState<UnitSystem>(() => {
    const saved = localStorage.getItem('empire-unit-system');
    if (saved === 'METRIC' || saved === 'IMPERIAL' || saved === 'BRITISH') return saved;
    
    // Default to Metric if not USA
    const locale = navigator.language;
    return locale.includes('US') ? 'IMPERIAL' : 'METRIC';
  });

  const setUnitSystem = (system: UnitSystem) => {
    setUnitSystemState(system);
    localStorage.setItem('empire-unit-system', system);
  };

  const toggleUnitSystem = () => {
    setUnitSystemState(prev => {
      let next: UnitSystem;
      if (prev === 'METRIC') next = 'IMPERIAL';
      else if (prev === 'IMPERIAL') next = 'BRITISH';
      else next = 'METRIC';
      
      localStorage.setItem('empire-unit-system', next);
      return next;
    });
  };

  const isMetric = unitSystem === 'METRIC';

  return (
    <UnitContext.Provider value={{ unitSystem, setUnitSystem, toggleUnitSystem, isMetric }}>
      {children}
    </UnitContext.Provider>
  );
}

export function useUnits() {
  const context = useContext(UnitContext);
  if (context === undefined) {
    throw new Error('useUnits must be used within a UnitProvider');
  }
  return context;
}
